<template>
  <section class="log-in">
    <form
      @submit.prevent="submitForm"
      class="log-in__form"
    >
      <Input
        id="LogInEmail"
        type="email"
        :label="$t('myPage.auth.email')"
        v-model="user.email"
        :placeholder="$t('myPage.auth.email')"
        autocomplete="email"
        :aria-label="$t('myPage.auth.email')"
        required
        class="log-in__input"
      />
      <Input
        id="LogInPassword"
        :label="$t('myPage.auth.password')"
        type="password"
        v-model="user.password"
        :placeholder="$t('myPage.auth.password')"
        required
        class="log-in__input"
      />
      <button
        type="submit"
        class="button button--medium button--primary"
      >
        {{ $t('myPage.auth.login') }}
      </button>
      <p
        v-if="errorMessage"
        class="log-in__error"
      >
        {{ errorMessage }}
      </p>
    </form>
  </section>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import Input from '@/components/BaseComponents/Input.vue'
  import { fetchCrmToken, getUserDetails } from '@/services/userService'
  import { useUserStore } from '@/stores/userStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useAuthStore } from '@/stores/oauthStore'

  interface LoginFormData {
    email: string
    password: string
  }

  export default defineComponent({
    name: 'LogIn',
    components: {
      Input,
    },
    props: {
      routeToPage: {
        type: String,
        required: true
      },
      bookingStarted: {
        type: Boolean,
        default: false
      },
      bookingNumber: {
        type: String,
        default: ''
      }
    },
    setup(props) {
      const router = useRouter()
      const userStore = useUserStore()
      const bookingStore = useBookingStore()
      const authStore = useAuthStore()
      const { t } = useI18n()

      const user = ref<LoginFormData>({
        email: '',
        password: '',
      })
      const errorMessage = ref<string | null>(null)

      const submitForm = async () => {
        try {
          // In booking flow, we should have a public token to upgrade
          const currentToken = authStore.token
          const token = props.bookingStarted && currentToken ? currentToken : undefined
          if (props.bookingStarted && !token) {
            console.warn('No public token found for upgrade during booking flow')
          }

          // Authenticate user with CRM token, potentially upgrading existing token
          const tokenResponse = await fetchCrmToken(
            user.value.email,
            user.value.password,
            token // Pass the public token for upgrade if we're in booking flow
          )
          
          // Store the new CRM token
          userStore.setToken(tokenResponse.access_token)
          userStore.setLoggedIn(true)
          userStore.setPassword(user.value.password)

          // Update auth store with CRM token type
          authStore.setToken(
            tokenResponse.access_token,
            Date.now() + (tokenResponse.expires_in * 1000),
            'crm',
            props.bookingNumber
          )

          // Get user details using CRM token
          const userDetailsData = await getUserDetails(tokenResponse.access_token)
          if (!userDetailsData?.data?.customer) {
            throw new Error('Invalid user details response')
          }
          userStore.setUser(userDetailsData.data.customer)

          // Handle booking flow if present
          if (props.bookingStarted && props.bookingNumber) {
            localStorage.setItem('pendingBookingNumber', props.bookingNumber)
            localStorage.setItem('bookingStarted', 'true')
            
            // Restore booking state if needed
            if (bookingStore.bookingNumber) {
              await bookingStore.fetchBookingData(bookingStore.bookingNumber)
            }
          }

          userStore.resetInactivityTimeout()

          // Navigate to appropriate page
          router.push({ 
            name: props.routeToPage,
            query: {
              ...(props.bookingNumber && { bookingNumber: props.bookingNumber }),
              ...(props.bookingStarted && { bookingStarted: 'true' })
            }
          })
        } catch (error: any) {
          console.error('Login error:', error)
          errorMessage.value = t('common.errors.auth.missingCredentials')
        }
      }

      return {
        user,
        errorMessage,
        submitForm,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .log-in {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    width: 100%;
    max-width: 600px;

    &__form {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__input {
      width: 100%;
    }

    &__error {
      color: red;
    }
  }
</style>
